/**
 * useCustomDomain Hook
 *
 * Encapsulates custom domain API calls with auto-polling for status updates.
 * Polling is automatically enabled when domain is in a transitional state.
 *
 * Usage:
 * ```tsx
 * const {
 *   domain,
 *   loading,
 *   error,
 *   setupDomain,
 *   verifyDomain,
 *   removeDomain,
 * } = useCustomDomain(projectId);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { customDomainApi, CustomDomainApiError } from '../services/customDomainApi';
import { POLL_INTERVAL } from '../constants';
import type {
  CustomDomain,
  CustomDomainStatus,
  CustomDomainSurface,
  DomainSetupResponse,
} from '../types/customDomain.types';

export { POLL_INTERVAL };

/** Statuses that require polling for updates */
const TRANSITIONAL_STATUSES: CustomDomainStatus[] = [
  'verifying_dns',
  'provisioning_cert',
  'configuring_lb',
];

const CACHE_PREFIX = 'segmently:custom-domain:';

type CachedCustomDomain = {
  cacheStatus?: 'domain';
  domain: string;
  status: CustomDomainStatus;
  domainId: string;
  cachedAt: string;
};

type CachedNoDomain = {
  cacheStatus: 'none';
  cachedAt: string;
};

/** Untrusted shape of a parsed cache entry — every field optional until validated. */
type CachedEntryRaw = Partial<Omit<CachedCustomDomain, 'cacheStatus'>> & {
  cacheStatus?: 'domain' | 'none';
};

type CachedDomainState =
  | { kind: 'miss' }
  | { kind: 'domain'; domain: CustomDomain }
  | { kind: 'none' };

const verifiedThisSession = new Set<string>();

function getCacheKey(projectId: string): string {
  return `${CACHE_PREFIX}${projectId}`;
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function domainFromCache(projectId: string, cached: CachedCustomDomain): CustomDomain {
  return {
    id: cached.domainId,
    domain: cached.domain,
    projectId,
    status: cached.status,
    dnsAuthorization: null,
    certificate: null,
    certificateMapEntry: null,
    createdAt: cached.cachedAt,
    updatedAt: cached.cachedAt,
    createdBy: 'session-cache',
  };
}

function readCachedDomainState(projectId: string): CachedDomainState {
  if (!projectId) return { kind: 'miss' };

  const storage = getSessionStorage();
  if (!storage) return { kind: 'miss' };

  try {
    const raw = storage.getItem(getCacheKey(projectId));
    if (!raw) return { kind: 'miss' };

    const cached = JSON.parse(raw) as CachedEntryRaw;
    if (cached.cacheStatus === 'none') {
      if (!cached.cachedAt) {
        storage.removeItem(getCacheKey(projectId));
        return { kind: 'miss' };
      }

      return { kind: 'none' };
    }

    if (!cached.domain || !cached.status || !cached.domainId || !cached.cachedAt) {
      storage.removeItem(getCacheKey(projectId));
      return { kind: 'miss' };
    }

    return { kind: 'domain', domain: domainFromCache(projectId, cached as CachedCustomDomain) };
  } catch {
    storage.removeItem(getCacheKey(projectId));
    return { kind: 'miss' };
  }
}

function writeCachedDomain(projectId: string, domain: CustomDomain): void {
  if (!projectId) return;

  const storage = getSessionStorage();
  if (!storage) return;

  const cached: CachedCustomDomain = {
    cacheStatus: 'domain',
    domain: domain.domain,
    status: domain.status,
    domainId: domain.id,
    cachedAt: new Date().toISOString(),
  };

  storage.setItem(getCacheKey(projectId), JSON.stringify(cached));
}

function writeCachedNoDomain(projectId: string): void {
  if (!projectId) return;

  const storage = getSessionStorage();
  if (!storage) return;

  const cached: CachedNoDomain = {
    cacheStatus: 'none',
    cachedAt: new Date().toISOString(),
  };

  storage.setItem(getCacheKey(projectId), JSON.stringify(cached));
}

/**
 * Check if status requires polling
 */
function shouldPoll(status: CustomDomainStatus | undefined): boolean {
  return status !== undefined && TRANSITIONAL_STATUSES.includes(status);
}

/**
 * Hook result interface
 */
export interface UseCustomDomainResult {
  /** Current domain data (null if not configured) */
  domain: CustomDomain | null;
  /** Initial loading state */
  loading: boolean;
  /** True when a project-scoped custom-domain session cache was used */
  hasSessionCache: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** Domain verification in progress */
  verifying: boolean;
  /** Domain removal in progress */
  removing: boolean;
  /** Domain setup in progress */
  setting: boolean;
  /** Setup a new custom domain */
  setupDomain: (domain: string) => Promise<DomainSetupResponse>;
  /** Trigger manual DNS verification */
  verifyDomain: () => Promise<void>;
  /** Remove custom domain */
  removeDomain: () => Promise<void>;
  /** Manually refresh status */
  refetch: () => Promise<void>;
}

/**
 * Custom Domain management hook
 *
 * @param projectId - Project ID to manage domain for
 * @param surface - Serving surface (v2.20.0): 'web' funnel domain (default)
 *   or 'article' domain; each surface holds ONE domain per project
 * @returns Hook result with domain state and actions
 */
export function useCustomDomain(
  projectId: string,
  surface: CustomDomainSurface = 'web',
): UseCustomDomainResult {
  // Session cache and dedupe are keyed per (project, surface)
  const cacheId = surface === 'article' ? `${projectId}:article` : projectId;

  // State
  const [domain, setDomain] = useState<CustomDomain | null>(() => {
    const cached = readCachedDomainState(cacheId);
    return cached.kind === 'domain' ? cached.domain : null;
  });
  const [loading, setLoading] = useState(() => readCachedDomainState(cacheId).kind === 'miss');
  const [hasSessionCache, setHasSessionCache] = useState(() => readCachedDomainState(cacheId).kind !== 'miss');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [setting, setSetting] = useState(false);

  // Polling interval ref
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mounted ref to prevent state updates after unmount
  const mountedRef = useRef(true);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Fetch current status
   */
  const fetchStatus = useCallback(async (options?: { background?: boolean }) => {
    if (!projectId) {
      setDomain(null);
      setLoading(false);
      setHasSessionCache(false);
      return;
    }

    if (!options?.background) {
      setLoading(true);
    }

    try {
      const result = await customDomainApi.getStatus(projectId, surface);

      if (!mountedRef.current) return;

      setDomain(result);
      setError(null);
      verifiedThisSession.add(cacheId);

      if (result) {
        writeCachedDomain(cacheId, result);
        setHasSessionCache(true);
      } else {
        writeCachedNoDomain(cacheId);
        setHasSessionCache(true);
      }

      // Start/stop polling based on status
      if (result && shouldPoll(result.status)) {
        // Start polling if not already
        if (!pollIntervalRef.current) {
          pollIntervalRef.current = setInterval(() => {
            fetchStatus({ background: true });
          }, POLL_INTERVAL);
        }
      } else {
        stopPolling();
      }
    } catch (err) {
      if (!mountedRef.current) return;

      if (err instanceof CustomDomainApiError) {
        if (err.code === 'DOMAIN_NOT_FOUND' || err.status === 404) {
          setDomain(null);
          setError(null);
          writeCachedNoDomain(cacheId);
          setHasSessionCache(true);
          verifiedThisSession.add(cacheId);
          stopPolling();
          return;
        }
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      if (mountedRef.current && !options?.background) {
        setLoading(false);
      }
    }
  }, [projectId, surface, cacheId, stopPolling]);

  /**
   * Setup a new custom domain
   */
  const setupDomain = useCallback(async (domainName: string): Promise<DomainSetupResponse> => {
    setSetting(true);
    setError(null);

    try {
      const response = await customDomainApi.setup(projectId, domainName, surface);

      // Refetch to get full domain object
      await fetchStatus();

      return response;
    } catch (err) {
      if (err instanceof CustomDomainApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setSetting(false);
      }
    }
  }, [projectId, surface, fetchStatus]);

  /**
   * Trigger manual DNS verification
   */
  const verifyDomain = useCallback(async (): Promise<void> => {
    setVerifying(true);
    setError(null);

    try {
      await customDomainApi.verify(projectId, surface);
      // Refetch to get updated status
      await fetchStatus();
    } catch (err) {
      if (err instanceof CustomDomainApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setVerifying(false);
      }
    }
  }, [projectId, surface, fetchStatus]);

  /**
   * Remove custom domain
   */
  const removeDomain = useCallback(async (): Promise<void> => {
    setRemoving(true);
    setError(null);

    try {
      await customDomainApi.remove(projectId, surface);

      if (mountedRef.current) {
        setDomain(null);
        setHasSessionCache(true);
        writeCachedNoDomain(cacheId);
        verifiedThisSession.add(cacheId);
        stopPolling();
      }
    } catch (err) {
      if (err instanceof CustomDomainApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setRemoving(false);
      }
    }
  }, [projectId, surface, cacheId, stopPolling]);

  /**
   * Initial fetch and cleanup
   */
  useEffect(() => {
    mountedRef.current = true;

    const cachedState = readCachedDomainState(cacheId);
    const cachedDomain = cachedState.kind === 'domain' ? cachedState.domain : null;
    const hasCachedState = cachedState.kind !== 'miss';

    setDomain(cachedDomain);
    setHasSessionCache(hasCachedState);

    if (!projectId) {
      setLoading(false);
      return undefined;
    }

    if (hasCachedState) {
      setLoading(false);
      // A cached transitional status (verifying_dns/provisioning_cert/configuring_lb)
      // must always re-check on (re)mount even when this session already verified
      // it once — the earlier mount's polling interval died with it (unmount clears
      // the ref), so trusting the stale cache here would silently freeze the UI on
      // an in-progress state until a full page reload. Launch Activation's
      // DomainConnectDrawer unmounts on close and remounts on reopen (AC-060), so
      // this path is reachable within a single session, not just across reloads.
      const cachedIsTransitional = cachedDomain !== null && shouldPoll(cachedDomain.status);
      if (!verifiedThisSession.has(cacheId) || cachedIsTransitional) {
        verifiedThisSession.add(cacheId);
        fetchStatus({ background: true });
      }
    } else {
      setLoading(true);
      fetchStatus();
    }

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [fetchStatus, stopPolling]);

  return {
    domain,
    loading,
    hasSessionCache,
    error,
    verifying,
    removing,
    setting,
    setupDomain,
    verifyDomain,
    removeDomain,
    refetch: fetchStatus,
  };
}

export function __resetCustomDomainSessionCacheForTests(): void {
  verifiedThisSession.clear();
}

export default useCustomDomain;
